// Asset pointers for the 8 stat screenshots used on Landing + /preview.
import winRateTrend from "@/assets/preview/win-rate-trend.png.asset.json";
import scorePerSet from "@/assets/preview/score-per-set.png.asset.json";
import matchRating from "@/assets/preview/match-rating.png.asset.json";
import statsTiles from "@/assets/preview/stats-tiles.png.asset.json";
import smartwatch from "@/assets/preview/smartwatch-stats.png.asset.json";
import performanceReport from "@/assets/preview/performance-report.png.asset.json";
import perGameSuccess from "@/assets/preview/per-game-success.png.asset.json";
import aiInsights from "@/assets/preview/ai-insights.png.asset.json";

export type PreviewScreen = {
  id: string;
  src: string;
  alt: { en: string; el: string };
  title: { en: string; el: string };
  caption: { en: string; el: string };
};

export const previewScreens: PreviewScreen[] = [
  {
    id: "stats-tiles",
    src: statsTiles.url,
    alt: {
      en: "Tennis Log dashboard summaries showing overall win rate, sets, games, hold rate, breaks won and current form",
      el: "Συνοπτικά στοιχεία του Tennis Log: ποσοστό νικών, σετ, games, hold rate, breaks και τρέχουσα φόρμα",
    },
    title: {
      en: "Your dashboard at a glance",
      el: "Το dashboard σου με μια ματιά",
    },
    caption: {
      en: "As you log matches, your dashboard builds a record of win rate, sets and games, hold rate, breaks won on return, and your current form.",
      el: "Καθώς καταγράφεις αγώνες, το dashboard συγκεντρώνει το ποσοστό νικών, τα σετ και games, το hold rate, τα breaks στην υποδοχή και την τρέχουσα φόρμα σου.",
    },
  },
  {
    id: "win-rate-trend",
    src: winRateTrend.url,
    alt: {
      en: "Win-rate trend line chart filtered by Singles, with format and year filters",
      el: "Γράφημα τάσης ποσοστού νικών φιλτραρισμένο σε Singles",
    },
    title: {
      en: "Win-rate trend over time",
      el: "Τάση ποσοστού νικών στον χρόνο",
    },
    caption: {
      en: "Filter by Singles / Doubles / Mixed and Friendly / Official to see how your recorded results change over time, then interpret the trend alongside opponent strength, format and other match context.",
      el: "Φιλτράρισμα ανά Μονά / Διπλά / Μικτά και Φιλικά / Επίσημα για να δεις αν πραγματικά βελτιώνεσαι, στασιμότητα, ή απλά παίζεις ευκολότερους αντιπάλους.",
    },
  },
  {
    id: "score-per-set",
    src: scorePerSet.url,
    alt: {
      en: "Per-set score entry with game-by-game hold/break grid",
      el: "Καταχώριση σκορ σετ με πλέγμα hold/break ανά game",
    },
    title: {
      en: "Game-by-game score entry",
      el: "Καταχώριση game-by-game",
    },
    caption: {
      en: "Tap each cell once for Hold, twice for Break. The app then computes hold %, break %, and serve-stability metrics with zero extra work.",
      el: "Tap σε κάθε κελί για Hold, διπλό tap για Break. Η εφαρμογή υπολογίζει αυτόματα hold %, break % και μετρικές σταθερότητας σερβίς.",
    },
  },
  {
    id: "match-rating",
    src: matchRating.url,
    alt: {
      en: "Match self-rating sliders for forehand, backhand, service, return, volley, drop shot, lob, stamina and more",
      el: "Sliders αυτο-αξιολόγησης αγώνα: forehand, backhand, σερβίς, υποδοχή, βολέ, drop shot, lob, αντοχή κ.ά.",
    },
    title: {
      en: "Rate every shot of your match (1–10)",
      el: "Βαθμολόγησε κάθε χτύπημα του αγώνα σου (1–10)",
    },
    caption: {
      en: "Three tabs — Summary, Detailed, Psychology — so the rating only takes a minute. Over many matches these sliders become your real strengths/weaknesses map.",
      el: "Τρεις καρτέλες — Σύνοψη, Λεπτομερής, Ψυχολογία — η αξιολόγηση παίρνει ένα λεπτό. Με πολλούς αγώνες, αυτά τα sliders γίνονται ο πραγματικός χάρτης των δυνατών και αδύναμων σημείων σου.",
    },
  },
  {
    id: "performance-report",
    src: performanceReport.url,
    alt: {
      en: "Performance radar chart comparing Me vs Opponent across 15 tennis skills",
      el: "Ραντάρ σύγκρισης Εγώ vs Αντίπαλος σε 15 δεξιότητες τένις",
    },
    title: {
      en: "Performance report — you vs your opponents",
      el: "Performance report — εσύ vs οι αντίπαλοί σου",
    },
    caption: {
      en: "Radar + side-by-side bars across 15 skills, filtered by opponent, format and year. The gap between your ratings and your opponents' ratings highlights differences worth investigating in practice and future matches.",
      el: "Ραντάρ + οριζόντιες μπάρες για 15 δεξιότητες, με φίλτρα αντιπάλου, format και έτους. Το κενό μεταξύ πράσινου (εσύ) και μπλε (αντίπαλοι) δείχνει ακριβώς τι να προπονήσεις.",
    },
  },
  {
    id: "per-game-success",
    src: perGameSuccess.url,
    alt: {
      en: "Per-game success bar chart showing win rate by game number across all sets",
      el: "Γράφημα επιτυχίας ανά game: ποσοστό νίκης ανά αριθμό game",
    },
    title: {
      en: "Per-game success — find your hidden bleed",
      el: "Επιτυχία ανά game — βρες πού χάνεις κρυφά",
    },
    caption: {
      en: "Are you losing the 3rd game of every set? The 7th? This chart finds the exact game number where your concentration drops, so warm-ups and routines can target it.",
      el: "Χάνεις πάντα το 3ο game του σετ; Το 7ο; Αυτό το γράφημα εντοπίζει το ακριβές game όπου πέφτει η συγκέντρωσή σου, ώστε ζέσταμα και ρουτίνες να το στοχεύουν.",
    },
  },
  {
    id: "smartwatch-stats",
    src: smartwatch.url,
    alt: {
      en: "Smartwatch stats: average calories, duration, BPM, max BPM and heart-rate zone bar chart",
      el: "Στατιστικά smartwatch: μέσες θερμίδες, διάρκεια, BPM, max BPM και ζώνες καρδιακών παλμών",
    },
    title: {
      en: "Smartwatch & heart-rate zones",
      el: "Smartwatch & ζώνες καρδιακών παλμών",
    },
    caption: {
      en: "Upload a screenshot from Apple / Garmin / Polar / Huawei watch and the app extracts calories, duration, average and max BPM, then averages your time in each heart-rate zone across all matches.",
      el: "Ανέβασε στιγμιότυπο από Apple / Garmin / Polar / Huawei ρολόι και η εφαρμογή εξάγει θερμίδες, διάρκεια, μέσο και μέγιστο BPM, και υπολογίζει τον μέσο χρόνο σε κάθε ζώνη παλμών.",
    },
  },
  {
    id: "ai-insights",
    src: aiInsights.url,
    alt: {
      en: "AI Insights panel listing patterns like 'Stronger return than opponent', 'Surface: clay', 'Napped at midday'",
      el: "Πίνακας AI Insights με μοτίβα όπως «Καλύτερη υποδοχή από αντίπαλο», «Επιφάνεια: χωμάτινο», «Μεσημεριανός ύπνος»",
    },
    title: {
      en: "AI Insights — patterns you would never spot alone",
      el: "AI Insights — μοτίβα που δεν θα ξεχώριζες ποτέ μόνος",
    },
    caption: {
      en: "Heuristic + AI analysis surfaces deltas like “when you nap at midday you win 100%” or “your return is +33pp on hard”. Then click Ask AI for a personal coaching narrative.",
      el: "Heuristic + AI ανάλυση αναδεικνύει μοτίβα όπως «όταν κοιμάσαι το μεσημέρι κερδίζεις 100%» ή «η υποδοχή σου είναι +33pp στα hard». Μετά πάτα Ask AI για προσωπική προπονητική αφήγηση.",
    },
  },
];

export type Lang = "en" | "el";
export function pickLang(language: string): Lang {
  return language?.toLowerCase().startsWith("el") ? "el" : "en";
}
